import { useEffect, useState } from "react";
import { forumProfileAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Trophy, Medal, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Achievements section for a profile page.
 * - selfView: shows the viewer's own achievements + a public/hidden master toggle
 * - userId: shows another user's achievements (only if they made them public)
 */
export default function ProfileAchievements({ selfView = false, userId = null }) {
  const [loading, setLoading] = useState(true);
  const [achievements, setAchievements] = useState([]);
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const req = selfView
      ? forumProfileAPI.getMyAchievements()
      : forumProfileAPI.getProfileAchievements(userId);
    req
      .then((r) => {
        setAchievements(r.data?.achievements || []);
        setIsPublic(r.data?.achievements_public ?? true);
      })
      .catch(() => setAchievements([]))
      .finally(() => setLoading(false));
  }, [selfView, userId]);

  const togglePublic = async (val) => {
    setIsPublic(val);
    setSaving(true);
    try { await forumProfileAPI.updateMySettings({ achievements_public: val }); toast.success(val ? "Achievements are public" : "Achievements hidden"); }
    catch { setIsPublic(!val); toast.error("Could not update"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Card className="border-border bg-card p-5" data-testid="profile-achievements">
        <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      </Card>
    );
  }

  // For other users: hide the whole section when private or empty
  if (!selfView && (!achievements.length)) return null;

  return (
    <Card className="border-border bg-card p-5" data-testid="profile-achievements">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold"><Trophy className="h-4 w-4 text-primary" /> Achievements</h2>
        {selfView && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{isPublic ? "Public" : "Hidden"}</span>
            <Switch checked={isPublic} onCheckedChange={togglePublic} disabled={saving} data-testid="profile-achievements-public-toggle" />
          </div>
        )}
      </div>

      {achievements.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">
          No achievements yet — finish on a podium or crack a track's Top 10 to earn some.
        </p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {achievements.map((a) => (
            <li key={a.id} className="flex items-center gap-3 rounded-md border border-border bg-secondary/30 p-3" data-testid={`profile-achievement-${a.id}`}>
              {a.type === "podium" ? <Medal className="h-5 w-5 shrink-0 text-amber-400" /> : <Trophy className="h-5 w-5 shrink-0 text-primary" />}
              <span className="text-sm">{a.label}</span>
            </li>
          ))}
        </ul>
      )}
      {selfView && achievements.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">Toggle individual achievements for the forum in Settings → Forum identity.</p>
      )}
    </Card>
  );
}
